import React, { useState, useEffect } from 'react';
import { Dialog, Box, Typography, Button, IconButton, CircularProgress } from '@mui/material';
import CloseIcon from '@material-ui/icons/Close';
import CheckIcon from '@material-ui/icons/Check';
import BusinessIcon from '@material-ui/icons/Business';

// ── Brand palette (theme.js) ─────────────────────────────────────────────────
const BRAND_TEAL = '#052F44';
const BRAND_ORANGE = '#FF6900';
const BRAND_GRADIENT = 'linear-gradient(135deg, #E7466E 0%, #FFAC46 100%)';
const FONT = 'Surt, sans-serif';

const ROLE_BADGE = {
    owner: { bg: '#FBEAF0', color: '#993556', border: '#F4C0D1' },
    peer: { bg: '#E6F1FB', color: '#185FA5', border: '#B5D4F4' },
    client: { bg: '#E1F5EE', color: '#0F6E56', border: '#9FE1CB' },
};

const getInitial = (name) => {
    const trimmed = (name || '').trim();
    return trimmed ? trimmed.charAt(0).toUpperCase() : 'W';
};

/**
 * OrgPickerDialog
 *
 * Props:
 *   open         {boolean}   — controls visibility
 *   orgs         {Array}     — list of { dq_insider_id, workspace_name, role, is_active }
 *   currentId    {string}    — dq_insider_id of the workspace the user is currently in
 *                             (highlighted + pre-selected; falls back to first when absent)
 *   loading      {boolean}   — disables interaction while selectOrg is in-flight
 *   expired      {boolean}   — shows token-expiry message and disables confirm
 *   onSelect     {function}  — called with (dq_insider_id, role) when user confirms
 *   onCancel     {function}  — called when user dismisses without selecting
 */
// A user may hold two memberships for the same workspace (peer + client), so a
// card is identified by workspace + role, not by dq_insider_id alone.
const orgKey = (o) => `${o.dq_insider_id}::${o.role || ''}`;

const OrgPickerDialog = ({ open, orgs = [], currentId = null, currentRole = null, loading = false, expired = false, onSelect, onCancel }) => {
    const [selectedKey, setSelectedKey] = useState(null);

    const activeOrgs = orgs.filter((o) => o.is_active !== false);

    // A user can hold two roles (peer + client) for the same workspace, so the
    // "current" row is matched on workspace + role, not dq_insider_id alone —
    // otherwise both rows look current and the wrong one gets pre-selected.
    const isCurrentOrg = (o) =>
        currentId && o.dq_insider_id === currentId && (!currentRole || o.role === currentRole);

    // Pre-select the workspace the user is currently in (so the highlight reflects
    // reality on reopen); fall back to the first one when there is no current
    // context (e.g. the login org-picker, before any workspace is active).
    useEffect(() => {
        if (open) {
            const current = activeOrgs.find(isCurrentOrg);
            setSelectedKey(current ? orgKey(current) : (activeOrgs[0] ? orgKey(activeOrgs[0]) : null));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, currentId, currentRole]);

    const disabled = loading || expired;

    const handleCardClick = (key) => {
        if (disabled) return;
        setSelectedKey(key);
    };

    const handleConfirm = () => {
        if (!selectedKey || disabled) return;
        const selected = activeOrgs.find((o) => orgKey(o) === selectedKey);
        if (selected) onSelect(selected.dq_insider_id, selected.role);
    };

    const handleCancel = () => {
        if (loading) return;
        setSelectedKey(null);
        if (onCancel) onCancel();
    };

    const badgeFor = (role) => ROLE_BADGE[role] || ROLE_BADGE.owner;

    return (
        <Dialog
            open={open}
            onClose={loading ? undefined : handleCancel}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: '18px', overflow: 'hidden', m: { xs: 2, sm: 4 }, width: { xs: 'calc(100% - 32px)', sm: '100%' } } }}
        >
            <Box sx={{ p: { xs: 2.5, sm: 3.5 }, position: 'relative' }}>
                <IconButton
                    aria-label="close"
                    onClick={handleCancel}
                    disabled={loading}
                    sx={{ position: 'absolute', right: 12, top: 12, color: '#9aa7ab' }}
                >
                    <CloseIcon />
                </IconButton>

                {/* ── Header ──────────────────────────────────────────────── */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', mb: 2.5 }}>
                    <Box
                        sx={{
                            width: 54, height: 54, borderRadius: '16px', background: BRAND_GRADIENT,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                            mb: 1.5, boxShadow: '0 6px 16px rgba(231,70,110,0.25)',
                        }}
                    >
                        <BusinessIcon sx={{ fontSize: 28 }} />
                    </Box>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: { xs: '19px', sm: '21px' }, color: BRAND_TEAL }}>
                        Select a workspace
                    </Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: '14px', color: '#6b7c84', mt: 0.75, maxWidth: 330, lineHeight: 1.5 }}>
                        Your account belongs to multiple workspaces. Choose one to continue.
                    </Typography>
                </Box>

                {/* ── Workspace list ──────────────────────────────────────── */}
                <Box
                    sx={{
                        display: 'flex', flexDirection: 'column', gap: 1.25,
                        maxHeight: { xs: '46vh', sm: 320 }, overflowY: 'auto',
                        // breathing room so card hover-shadow / focus ring isn't clipped
                        mx: -0.5, px: 0.5, py: 0.5,
                        '&::-webkit-scrollbar': { width: '6px' },
                        '&::-webkit-scrollbar-thumb': { backgroundColor: '#d9dee0', borderRadius: '3px' },
                    }}
                >
                    {activeOrgs.map((org) => {
                        const key = orgKey(org);
                        const isSelected = selectedKey === key;
                        const isCurrent = isCurrentOrg(org);
                        const name = org.workspace_name || 'My Workspace';
                        const badge = badgeFor(org.role);
                        return (
                            <Box
                                key={key}
                                onClick={() => handleCardClick(key)}
                                role="button"
                                tabIndex={0}
                                aria-pressed={isSelected}
                                onKeyDown={(e) => e.key === 'Enter' && handleCardClick(key)}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1.25,
                                    p: { xs: '9px 12px', sm: '10px 14px' }, borderRadius: '13px',
                                    border: isSelected ? `2px solid ${BRAND_ORANGE}` : '1.5px solid var(--brand-border, #e6e9ea)',
                                    // Theme-aware: unselected card stays #fff in light mode, flips to the
                                    // dark surface in dark mode (otherwise white card + forced-light text = invisible).
                                    background: isSelected ? 'rgba(255,105,0,0.06)' : 'var(--brand-surface, #fff)',
                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                    opacity: disabled && !isSelected ? 0.55 : 1,
                                    transition: 'all 0.18s ease',
                                    '&:hover': disabled ? {} : {
                                        borderColor: BRAND_ORANGE,
                                        boxShadow: '0 4px 14px rgba(255,105,0,0.14)',
                                        transform: 'translateY(-1px)',
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 38, height: 38, borderRadius: '11px', flexShrink: 0,
                                        backgroundColor: BRAND_TEAL, color: '#fff',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontFamily: FONT, fontWeight: 700, fontSize: '16px',
                                    }}
                                >
                                    {getInitial(name)}
                                </Box>

                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        title={name}
                                        sx={{
                                            fontFamily: FONT, fontWeight: 600, fontSize: '14.5px', color: BRAND_TEAL,
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {name}
                                    </Typography>
                                    {isCurrent && (
                                        <Typography sx={{ fontFamily: FONT, fontSize: '11px', fontWeight: 600, color: BRAND_ORANGE, mt: '1px' }}>
                                            Current workspace
                                        </Typography>
                                    )}
                                </Box>

                                <Box
                                    component="span"
                                    sx={{
                                        flexShrink: 0, px: '10px', py: '3px', borderRadius: '20px',
                                        fontFamily: FONT, fontWeight: 700, fontSize: '11.5px', textTransform: 'capitalize',
                                        backgroundColor: badge.bg, color: badge.color, border: `1px solid ${badge.border}`,
                                    }}
                                >
                                    {org.role || 'member'}
                                </Box>

                                <Box sx={{ width: 22, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                                    {isSelected && (
                                        <Box
                                            sx={{
                                                width: 22, height: 22, borderRadius: '50%', backgroundColor: BRAND_ORANGE,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                                            }}
                                        >
                                            <CheckIcon sx={{ fontSize: 14 }} />
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>

                {expired && (
                    <Typography sx={{ fontFamily: FONT, color: BRAND_ORANGE, textAlign: 'center', fontSize: '13px', mt: 2 }}>
                        Session expired. Please log in again.
                    </Typography>
                )}

                {/* ── Actions ─────────────────────────────────────────────── */}
                <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' }, gap: 1.25, mt: 2.5 }}>
                    <Button
                        onClick={handleCancel}
                        variant="outlined"
                        disabled={loading}
                        sx={{
                            borderRadius: '20px', flex: { xs: 1, sm: 'none' }, minWidth: { xs: 0, sm: 100 }, py: 0.6, px: 2.5,
                            fontFamily: FONT, fontWeight: 700, fontSize: '13.5px', textTransform: 'none',
                            borderColor: '#cfd6d8', color: '#6b7c84',
                            '&:hover': { borderColor: '#9aa7ab', backgroundColor: 'transparent' },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        variant="contained"
                        disableElevation
                        disabled={!selectedKey || disabled}
                        startIcon={loading ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : null}
                        sx={{
                            borderRadius: '20px', flex: { xs: 1, sm: 'none' }, minWidth: { xs: 0, sm: 128 }, py: 0.6, px: 3,
                            fontFamily: FONT, fontWeight: 700, fontSize: '13.5px', textTransform: 'none',
                            backgroundColor: BRAND_ORANGE, boxShadow: '0 3px 10px rgba(255,105,0,0.25)',
                            '&:hover': { backgroundColor: '#e85f00', boxShadow: '0 3px 10px rgba(255,105,0,0.3)' },
                            '&.Mui-disabled': { backgroundColor: '#f0c3a3', color: '#fff', boxShadow: 'none' },
                        }}
                    >
                        {loading ? 'Switching…' : 'Continue'}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
};

export default OrgPickerDialog;
